import {Component, DestroyRef, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, NgForm} from '@angular/forms';
import {TABS, ATTACHMENT_PLAN_PDF, PDF_CREATION_METHOD, ATTACHMENT_CSD, LAND_CSV, PREVIEW_EXTENSIONS_CONSTRAINT} from '../common/constants';
import {NgEventBus} from 'ng-event-bus';
import {AdditionalsViewModel, AdditionalViewModel} from '../common/models/additionals-view.model';
import {AttachmentModel} from 'src/app/common/models/attachment-model';
import {PlanAttachment} from '../common/models/plan-attachment.model';
import {AdditionalsService} from './additionals.service';
import {LookupService} from '../common/service/lookup.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {AlertModalService} from '../common/component/modal/alert-modal/alert-modal.service';
import {AttachmentsModalService} from '../common/component/modal/attachments-modal/attachments-modal.service';
import {ConfirmationModalService} from '../common/component/modal/confirmation-modal/confirmation-modal.service';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {EvidenceValidationService} from '../common/component/evidence-validation/evidence-validation.service';
import {AttachmentValidationService} from '../common/service/attachment-validation.service';
import {EvidenceValidationComponent} from '../common/component/evidence-validation/evidence-validation.component';
import {NLR_TABS as LeftComponentTabsIndex} from '../common/constants';
import {NlrHasPermissionsPipe} from '../common/pipe/nlr-has-permissions.pipe';
import {getMimeType} from '../common/service/mime-type.utils';
import {catchError, concatMap, EMPTY, map, throwError} from 'rxjs';
import {NlrFormFieldHasErrorPipe} from '../../app/common/pipe/nlr-form-field-has-error.pipe';
import {NlrPatternDirective} from '../common/directive/nlr-pattern.directive';
import {NlrUppercaseDirective} from '../common/directive/nlr-uppercase.directive';
import {PATTERNS} from '../common/constants';
import {Router} from '@angular/router';
import {PermissionsService} from '../common/service/permissions.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgSelectModule} from '@ng-select/ng-select';

@Component({
    selector: 'nlr-additionals',
    imports: [
        FormsModule,
        CommonModule,
        NlrHasPermissionsPipe,
        NlrFormFieldHasErrorPipe,
        NlrPatternDirective,
        NlrUppercaseDirective,
        EvidenceValidationComponent,
        NgSelectModule
    ],
    templateUrl: './additionals.html',
    styleUrl: './additionals.component.scss'
})
export class AdditionalsComponent implements OnInit {
    @ViewChild('additionalsForm')
    additionalsForm: NgForm;

    title: string = TABS.ADDITIONALS;
    initialised = false;
    applicationForTitlesLodged = false;
    originalApplicationForTitlesLodged = false;
    originalApplicationForTitlesDocumentIdentifier = null;
    additionalsViewModel: AdditionalsViewModel = {};
    additionalsPermissions = ['EDIT_ADDITIONALS', 'VIEW_ADDITIONALS'];
    consentDesc: string = 'I consent to the release of this plan to the planning authority';
    lodgeAdditionalsEnabled = false;
    attachmentTypes = [];
    attachmentTypeOptions = [];
    attachmentTypeLookups = [];
    documentPattern = PATTERNS.DOCUMENT_PATTERN;

    constructor(
        private eventBus: NgEventBus,
        private additionalsService: AdditionalsService,
        private alertModalService: AlertModalService,
        private lookupService: LookupService,
        private attachmentsModalService: AttachmentsModalService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private notificationAlertsService: NotificationAlertsService,
        private confirmationModalService: ConfirmationModalService,
        private attachmentValidationService: AttachmentValidationService,
        private evidenceValidationService: EvidenceValidationService,
        private router: Router,
        private permissionsService: PermissionsService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit(): void {
        this.eventBus
            .on(`nlr:tabChange:${LeftComponentTabsIndex.ADDITIONALS}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} controller initiateAdditionalsContent`);
                this.loadAdditionals(true);

                this.attachmentTypeLookups = this.lookupService.getLookups('attachmentType');

                this.additionalsService.getAttachmentTypes().subscribe(data => {
                    this.attachmentTypes = data;
                    this.lodgeAdditionalsEnabled = this.router.url.includes('additionals');
                    this.updateAttachmentTypeOptions();
                });
            });
    }

    updateAttachmentTypeOptions() {
        this.attachmentTypeOptions = [];

        const newAdditionalDocuments = this.getNewAdditionalDocuments();
        const selectedAttachmentTypes = newAdditionalDocuments.map(v => v.attachmentType).filter(v => v);

        this.attachmentTypeOptions = this.attachmentTypes
            .filter(attachmentTypeObject => attachmentTypeObject.additionalPermitted)
            .filter(attachmentTypeObject => !selectedAttachmentTypes.includes(attachmentTypeObject.attachmentType))
            .map(attachmentTypeObject => {
                return {type: attachmentTypeObject.attachmentType, name: this.getAttachmentName(attachmentTypeObject.attachmentType)};
            });
    }

    loadAdditionals(copyNewAdditionals: boolean) {
        const subs = this.additionalsService.getAdditionals().subscribe({
            next: data => {
                const applicationForTitlesDocumentIdentifier = '';
                this.additionalsViewModel = data;
                this.additionalsViewModel.newAdditionalDocuments =
                    copyNewAdditionals && this.additionalsViewModel && this.additionalsViewModel.newAdditionalDocuments
                        ? structuredClone(this.additionalsViewModel.newAdditionalDocuments)
                        : [];
                this.updateAttachmentTypeOptions();

                if (this.initialised) {
                    this.additionalsViewModel.applicationForTitlesDocumentIdentifier = applicationForTitlesDocumentIdentifier;
                } else {
                    this.applicationForTitlesLodged = !!this.additionalsViewModel.applicationForTitlesDocumentIdentifier;
                    this.originalApplicationForTitlesLodged = this.applicationForTitlesLodged;
                    this.originalApplicationForTitlesDocumentIdentifier = this.additionalsViewModel.applicationForTitlesDocumentIdentifier;
                }
            },
            error: errors => {
                const errMsg = 'Error loading additionals';
                console.debug(this.title, errMsg, errors);
                this.alertModalService.createAlertMessage(errMsg);
            }
        });

        this.eventBus.cast('nlr:busy', subs);
    }

    isPlanReleaseConsentApplicable() {
        return this.additionalsViewModel !== undefined && this.additionalsViewModel.planReleaseConsentApplicable;
    }

    getNewAdditionalDocuments() {
        return this.additionalsViewModel.newAdditionalDocuments || [];
    }

    getAttachmentTypeOptions(attachmentType?: string) {
        const newAdditionalDocuments = this.getNewAdditionalDocuments();
        const selectedAttachmentTypes = newAdditionalDocuments.map(v => v.attachmentType).filter(v => v);
        return this.attachmentTypes.filter(v => v.additionalPermitted && (v === attachmentType || !selectedAttachmentTypes.includes(v)));
    }

    updateSelectedAttachType(option: any, index: number) {
        this.additionalsViewModel.newAdditionalDocuments[index].details = undefined;
        this.additionalsViewModel.newAdditionalDocuments[index].fileLocation = undefined;
        this.additionalsViewModel.newAdditionalDocuments[index].fileName = undefined;
        this.additionalsViewModel.newAdditionalDocuments[index].attachmentType = option.type;
        this.additionalsViewModel.newAdditionalDocuments[index].attachmentTypeSelect = {attachmentType: option.type, name: option.name};
        this.updateAttachmentTypeOptions();
    }

    getAttachmentName(attachmentType: string) {
        if (attachmentType) return this.attachmentTypeLookups.find(v => v.id === attachmentType).name;
    }

    addAdditionalDocument() {
        if (this.additionalsViewModel !== undefined) {
            if (this.additionalsViewModel.newAdditionalDocuments === undefined) {
                this.additionalsViewModel.newAdditionalDocuments = [];
            }

            this.additionalsViewModel.newAdditionalDocuments = [
                ...this.additionalsViewModel.newAdditionalDocuments,
                {
                    attachmentType: '',
                    fileName: undefined,
                    details: '',
                    dateTimeAdded: null,
                    attachmentTypeSelect: {attachmentType: '', name: 'Select'}
                }
            ];
        }
    }

    triggerAttachFileModal(attachmentElement: any) {
        console.debug(`${this.title} controller triggerAttachFile`);
        const currentAttachmentConfiguration = this.attachmentTypes.filter(config => {
            return config.attachmentType === attachmentElement.attachmentType;
        })[0];

        const checkPdfSignature =
            currentAttachmentConfiguration.attachmentType === ATTACHMENT_PLAN_PDF &&
            [PDF_CREATION_METHOD.SURVEYOR_PORTAL, PDF_CREATION_METHOD.DIGITAL_PLAN].includes(this.getPlanModel().pdfCreationMethod);

        this.attachmentsModalService
            .showModal(currentAttachmentConfiguration.extensions, 'Other', this.getPlanModel().attachments, checkPdfSignature)
            .pipe(
                concatMap((data: AttachmentModel | string) => {
                    if (typeof data === 'string') {
                        return EMPTY;
                    } else {
                        return this.attachmentValidationService.validateAttachment(
                            attachmentElement,
                            currentAttachmentConfiguration.attachmentType,
                            data
                        );
                    }
                }),
                catchError(error => {
                    console.error('Error validating the uploaded file ' + error);
                    const errorMessage = 'System error while validating the attached file';
                    this.alertModalService.createAlertMessage(errorMessage);
                    return throwError(() => {
                        new Error(error);
                    });
                })
            )
            .subscribe(result => {
                if (result !== undefined) {
                    if (
                        (attachmentElement.attachmentType == ATTACHMENT_CSD || attachmentElement.attachmentType == LAND_CSV) &&
                        this.planModelService
                            .getModel()
                            .attachments.find(a => a.attachmentType == attachmentElement.attachmentType && a.optionality === 'Mandatory')
                    ) {
                        this.additionalsViewModel.newPlanLots = this.attachmentValidationService.getTotalLotCount(attachmentElement.attachmentType);
                    }
                }
                console.log(result);
            });
    }

    clearAdditional(additionalAttachment: any) {
        if (this.additionalsViewModel.newAdditionalDocuments !== undefined) {
            this.additionalsViewModel.newAdditionalDocuments.splice(
                this.additionalsViewModel.newAdditionalDocuments.indexOf(additionalAttachment),
                1
            );
        }

        this.updateAttachmentTypeOptions();

        if (
            additionalAttachment.fileName &&
            (additionalAttachment.attachmentType == ATTACHMENT_CSD || additionalAttachment.attachmentType == LAND_CSV) &&
            this.planModelService
                .getModel()
                .attachments.find(a => a.attachmentType == additionalAttachment.attachmentType && a.optionality === 'Mandatory')
        ) {
            this.additionalsViewModel.newPlanLots = undefined;
            this.attachmentValidationService.clearValidationInfo(additionalAttachment.attachmentType);
        }
    }

    preview(additionalViewModel: AdditionalViewModel) {
        if (additionalViewModel.fileLocation !== undefined && additionalViewModel.fileLocation !== null) {
            const uidWithPrefix = additionalViewModel.fileLocation.slice(additionalViewModel.fileLocation.lastIndexOf('/Other/'));

            this.additionalsService
                .getSignedUrlForRetrieval(uidWithPrefix)
                .pipe(
                    concatMap((signedUrl: string) => {
                        return this.additionalsService.getAdditionalAttachment(signedUrl);
                    })
                )
                .subscribe({
                    next: (data: ArrayBuffer) => {
                        if (data) {
                            this.displayAdditional(data, additionalViewModel);
                        } else {
                            console.error('Attachment could not be retrieved');
                        }
                    },
                    error: error => {
                        console.error(error);
                    }
                });
        } else {
            const request = {
                planLodgementId: this.getPlanModel().id,
                evidenceIndex: additionalViewModel.evidenceIndex
            };
            this.additionalsService.getAdditionalImage(request).subscribe({
                next: (data: ArrayBuffer) => {
                    if (data) {
                        this.displayAdditional(data, additionalViewModel);
                    } else {
                        console.error('Attachment could not be retrieved');
                    }
                },
                error: error => {
                    console.error(error);
                }
            });
        }
    }

    displayAdditional(data: any, additionalViewModel: any) {
        if (data) {
            const file = new Blob([data], getMimeType(additionalViewModel.fileName));
            const fileURL = URL.createObjectURL(file);
            const params = ['height=' + screen.height, 'width=' + screen.width, 'fullscreen=yes'].join(',');
            let popup = window.open(fileURL, '_blank', params);
            popup.moveTo(0, 0);
            popup.onload = function () {
                URL.revokeObjectURL(fileURL);
            };
        } else {
            console.error('Attachment could not be retrieved');
        }
    }

    canPreview(fileName: string) {
        if (fileName === undefined) {
            return false;
        }
        return PREVIEW_EXTENSIONS_CONSTRAINT.indexOf(fileName.slice(fileName.lastIndexOf('.')).toUpperCase()) !== -1;
    }

    canLodge() {
        if (!this.additionalsViewModel) {
            return false;
        }

        if (this.additionalsForm && !this.additionalsForm.valid) {
            return false;
        }

        return (
            (this.additionalsViewModel.newAdditionalDocuments && this.additionalsViewModel.newAdditionalDocuments.length > 0) ||
            (!this.additionalsViewModel.planReleaseConsent &&
                this.additionalsViewModel.postReleaseConsentDate === null &&
                this.additionalsViewModel.postReleaseConsent) ||
            (this.applicationForTitlesLodged &&
                this.additionalsViewModel.applicationForTitlesDocumentIdentifier !== this.originalApplicationForTitlesDocumentIdentifier) ||
            this.applicationForTitlesLodged !== this.originalApplicationForTitlesLodged
        );
    }

    postReleaseConsentGranted() {
        return this.additionalsViewModel.postReleaseConsentDate !== null;
    }

    isAdditionalDocumentDisabled() {
        return !this.lodgeAdditionalsEnabled || this.getAttachmentTypeOptions().length === 0 || this.isReadonly();
    }

    isCancelDisabled() {
        return !this.lodgeAdditionalsEnabled || this.isReadonly();
    }

    postPlanReleaseConsentHandler() {
        return !this.isReadonly() && (this.additionalsViewModel.postReleaseConsent = !this.additionalsViewModel.postReleaseConsent);
    }

    lodge() {
        this.additionalsForm.onSubmit(undefined);

        if (this.additionalsForm.invalid) {
            this.notificationAlertsService.addMessage({
                subType: 'AdditionalsForm',
                text: 'The additionals page is incomplete or data entered is invalid!'
            });
            return;
        }

        let hasMissingFiles = false;

        if (this.additionalsViewModel.newAdditionalDocuments !== undefined) {
            this.additionalsViewModel.newAdditionalDocuments.forEach((newAdditionalDocument: AdditionalViewModel) => {
                if (newAdditionalDocument.fileName === undefined) hasMissingFiles = true;
            });
        }

        if (hasMissingFiles) {
            this.alertModalService.createAlertMessage('Please upload missing Additional files', 'Cancel');
            return;
        }

        if (!this.applicationForTitlesLodged) {
            this.additionalsViewModel.applicationForTitlesDocumentIdentifier = null;
        }

        this.confirmationModalService
            .showModal({
                title: 'Confirm Additionals Submission',
                confirmationText: 'Have you included all relevant information and wish to continue with this submission?'
            })
            .pipe(
                map((action: string) => {
                    if (action === 'yes') {
                        this.additionalsViewModel.planLodgementId = this.getPlanModel().id;
                        this.lodgeAdditionals();
                        return EMPTY;
                    } else {
                        return EMPTY;
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    lodgeAdditionals() {
        const subscription = this.additionalsService.lodgeAdditionals(this.additionalsViewModel).subscribe(response => {
            const errorMessages = response;
            if (errorMessages !== undefined) {
                if (errorMessages.length > 0) {
                    this.alertModalService.createAlertMessage(errorMessages, 'Ok', 'Error occurred during submission', 'error', [], '', '', 'lg');
                } else {
                    this.processSuccessModal();
                }
            } else {
                const errorMessage = 'Error submitting additionals';
                this.alertModalService.createAlertMessage(errorMessage);
            }
        });
        this.eventBus.cast('nlr:busy', subscription);
    }

    processSuccessModal() {
        this.alertModalService
            .createAlertMessage('The Release Consent and Additionals have been submitted', 'OK', 'Success', 'success')
            .subscribe(action => {
                if (action === 'yes') {
                    this.router.navigate(['/plan-dashboard']);
                }
            });
    }

    cancel() {
        console.debug(`${this.title} controller cancel`);
        this.planLodgementService.cancel();
        this.attachmentValidationService.clearAllValidationInfo();
    }

    hasValidationErrorsOrWarnings(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationErrorsOrWarnings(attachment);
    }

    hasValidationErrors(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationErrors(attachment);
    }

    hasValidationWarnings(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationWarnings(attachment);
    }

    isReadonly(): boolean {
        return !this.lodgeAdditionalsEnabled || !this.permissionsService.hasEditPermission(this.additionalsPermissions);
    }

    getPlanPurposeLotSync() {
        return this.getPlanModel().planPurpose?.lotSync ?? false;
    }

    private getPlanModel() {
        return this.planModelService.getModel();
    }
}
